import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ChartLine, MagnifyingGlass, Sparkle, Eye, Database,
  CaretLeft, ArrowRight, Lightning, Cursor, Clock, Bell,
  Crown, Rocket, Gear, ChartBar, Target, Check, Trophy,
} from '@phosphor-icons/react';

const handleLogin = () => {
  const redirectUrl = window.location.origin + '/dashboard';
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const STEP_META = [
  { icon: Rocket, color: '#3B82F6' },
  { icon: MagnifyingGlass, color: '#10B981' },
  { icon: Trophy, color: '#F59E0B' },
  { icon: Target, color: '#EC4899' },
  { icon: Sparkle, color: '#F59E0B' },
  { icon: Eye, color: '#A855F7' },
  { icon: ChartBar, color: '#10B981' },
  { icon: Database, color: '#06B6D4' },
  { icon: Lightning, color: '#F59E0B' },
  { icon: Crown, color: '#F59E0B' },
];

const CONTENT = {
  pt: {
    badge: 'Guia oficial · 6 min de leitura',
    heroTitle1: 'Como usar o',
    heroSub: 'Do primeiro acesso ao teu primeiro funil clonado em menos de 1 hora. Segue os 10 passos abaixo na ordem — vais ver o poder real da plataforma.',
    back: 'Voltar',
    visualTip: 'Dica visual',
    quickRefTitle: 'Referência Rápida',
    quickRefSub: 'Atalhos e dicas para tirares o máximo',
    urlsTitle: 'URLs aceites',
    urls: ['✓ Páginas de venda (qualquer plataforma)', '✓ Landing pages (Hotmart, Kiwify, Eduzz, etc)', '✓ Sites próprios (Wordpress, ClickFunnels)', '✗ Sites com login obrigatório', '✗ Apps nativas (só web)'],
    freqTitle: 'Frequência',
    freq: ['• Análise instantânea (~30s)', '• Monitorização: cada 6h', '• Agenda CRM de follow-ups: todas as manhãs', '• Resumo semanal por email (segunda-feira)', '• Alertas: imediatos quando algo muda'],
    notifTitle: 'Notificações',
    notifs: ['📧 Email de boas-vindas (registo)', '📧 Confirmação de pagamento', '📧 Agenda diária de follow-ups do CRM (só quando há pendentes)', '📧 Resumo semanal (segunda-feira)', '📧 Alertas de mudanças nos monitores'],
    settingsTitle: 'Definições',
    settingsItems: ['• Gerir subscrição (Stripe Portal)', '• Exportar todos os teus dados (GDPR)', '• Eliminar conta permanentemente', '• Atualizar método de pagamento'],
    ctaTitle: 'Pronta para o teu primeiro funnel hack?',
    ctaSub: '7 dias grátis. Análise ilimitada. Cancela quando quiseres.',
    ctaBtn: 'Começar trial grátis',
    footerHome: 'Início', footerPricing: 'Preços', footerFaq: 'FAQ',
    steps: [
      {
        title: 'Cria a tua conta em 30 segundos',
        intro: 'É grátis começar — 7 dias de trial em qualquer plano pago. Sem cartão obrigatório no início.',
        items: ['Clica em "Entrar" no topo e usa a tua conta Google', 'És redireccionada ao Dashboard automaticamente', 'O teu plano Free fica activo de imediato (com 3 monitores grátis)'],
        screenshot: 'No primeiro acesso, vais ver um assistente de boas-vindas que te leva da análise à primeira geração de copy em 4 passos.',
      },
      {
        title: 'Analisa o teu primeiro funil concorrente',
        intro: 'O Funnel Spy é o coração da app. Cola qualquer URL e a IA disseca em segundos.',
        items: ['No menu lateral, clica em "Funnel Spy"', 'Cola o URL completo da página de vendas (ex: https://exemplo.com/oferta)', 'Clica em "Analisar Funil" — leva ~30 segundos', 'Vais ver: headlines extraídas, ofertas detectadas, estrutura do funil, score de qualidade, conversão estimada e ticket médio', 'No fim, clica em "Guardar na Biblioteca" para revisitar depois'],
        screenshot: 'A IA também detecta order bumps, upsells, downsells e elementos de prova social automaticamente.',
      },
      {
        title: 'Avalia páginas com o Winner Score™ (e ganha créditos)',
        intro: 'O Winner Score™ avalia qualquer página em 25+ indicadores (headline, oferta, prova, risco) e devolve um score 0-100 com plano de correções.',
        items: ['No menu, clica em "Winner Score" e cola o URL (teu ou de um concorrente)', 'Recebes o score, pontos fortes, fraquezas e potencial de lucro/escala', 'Ativa "Publicar anonimamente no Hall of Winners" — análises com score ≥ 75 ficam no ranking público (URL ocultado)', '🏆 Bónus: cada publicação forte dá-te +2 créditos grátis (máx 3/dia)', 'Usa o Hall of Winners público para veres os padrões dos melhores funis por nicho'],
        screenshot: 'Analisa a TUA página antes de investir em tráfego — o plano de correções diz-te exatamente o que melhorar.',
      },
      {
        title: 'Descobre funis vencedores no teu nicho (Discovery)',
        intro: 'Não sabes que concorrentes analisar? O Discovery encontra por ti os funis e anúncios que estão a escalar no teu nicho.',
        items: ['No menu, clica em "Discovery" e escreve o teu nicho (ex: "emagrecimento", "renda extra")', 'A IA devolve os players relevantes com queries de pesquisa prontas', 'Cada resultado tem 4 botões directos: FB Ads, Google, YT Ads e TikTok — vês os anúncios activos deles em 1 clique', 'Os "Atalhos manuais" abrem as bibliotecas de anúncios Meta, Google/YouTube e TikTok já filtradas pelo teu nicho', 'Encontraste um funil interessante? Cola o URL no Funnel Spy e disseca-o'],
        screenshot: 'Discovery + Funnel Spy juntos = pipeline infinito de inspiração validada pelo mercado.',
      },
      {
        title: 'Gera o teu próprio copy persuasivo',
        intro: 'O AI Composer cria headlines, VSLs, emails e bullets baseados em copywriting clássico (Dan Kennedy, Russell Brunson, etc).',
        items: ['No menu, clica em "AI Composer"', 'Escolhe o tipo: Headlines, VSL Script, Email Sequence, Bullets Persuasivos', 'Descreve o produto/oferta em 1-2 frases', 'A IA gera 3-5 variações em ~15 segundos', 'Copia, edita ou regenera quantas vezes quiseres'],
        screenshot: 'Para resultados ainda melhores, primeiro analisa um funil concorrente e usa as insights como contexto.',
      },
      {
        title: 'Monitoriza concorrentes 24/7 (automatizado)',
        intro: 'O sistema verifica os funis dos teus concorrentes a cada 6 horas e alerta-te quando eles mudam alguma coisa.',
        items: ['No menu, clica em "Monitoring"', 'Adiciona um URL para monitorizar (URL da página de vendas)', 'Dá-lhe um nome ("Concorrente A — Curso Vendas") para identificar', 'A app captura snapshots periódicos automaticamente', 'Recebes email quando há mudanças (headlines, preços, estrutura)', 'Clica num monitor para ver Timeline visual estilo "diff" — vês exactamente o que mudou'],
        screenshot: 'Free: 3 monitores · Pro: 25 monitores · Agency: ilimitado. Tudo verificado de 6h em 6h.',
      },
      {
        title: 'Gere os teus leads no CRM (com follow-ups e IA)',
        intro: 'Um CRM completo com pipeline Kanban, valor de negócio por contacto e agenda de follow-ups para nunca perderes uma venda.',
        items: ['No menu, clica em "CRM" e adiciona contactos (ou importa um CSV de outra ferramenta)', 'Arrasta os cards entre etapas: Novo → Contactado → Qualificado → Proposta → Ganho', 'Define o valor (€) e a data da próxima ação em cada contacto — o topo mostra o pipeline total e a previsão ponderada', 'Badges "Atrasado"/"Hoje" mostram quem precisa de atenção; usa o filtro de follow-ups', 'Dentro de cada contacto: timeline de atividade + botão "Email de follow-up com IA" gera o email pronto a enviar', 'Todas as manhãs recebes um email com a tua agenda de follow-ups do dia (só se houver algo pendente)'],
        screenshot: 'O score IA (botão ✨ em cada card) avalia a probabilidade de fecho de 0-100 com justificação.',
      },
      {
        title: 'Constrói a tua biblioteca de inspiração',
        intro: 'Cada análise pode ser guardada. Cria uma coleção privada dos melhores funis do teu nicho.',
        items: ['Após qualquer análise no Funnel Spy, clica em "Guardar na Biblioteca"', 'Acede via "Library" no menu lateral', 'Filtra por nicho, data ou score', 'Revisita análises antigas para inspiração em novos lançamentos'],
        screenshot: 'As tuas análises são totalmente privadas. Nenhum outro utilizador vê o que estás a estudar.',
      },
      {
        title: 'Créditos, presentes e afiliados',
        intro: 'Não queres subscrição? Usa credit packs. Queres ganhar dinheiro com a app? Torna-te afiliado.',
        items: ['Em "Créditos": packs de €9 a €197 (1 crédito = 1 geração premium, sem expiração)', 'Modo "Para oferecer": compra um pack para outra pessoa — ela recebe um código por email', 'Ganha créditos grátis: +2 por publicares análises no Hall of Winners, +10 por cada Pro signup via Battle Card', 'Em "Afiliados": obténs o teu link único e ganhas comissões recorrentes por cada subscritor que trouxeres'],
        screenshot: 'O leaderboard de afiliados mostra os top promotores do mês — com botões de partilha de 1 clique.',
      },
      {
        title: 'Faz upgrade quando estiveres pronta',
        intro: '7 dias grátis em qualquer plano pago. Sem compromisso longo, cancelas com 1 clique.',
        items: ['No menu lateral clica em "Pricing"', 'Compara Pro (€27/mês, 25 monitores) e Agency (€97/mês, ilimitado + multi-marca)', 'Clica no botão do plano escolhido para iniciar o trial', 'Adicionas cartão (Stripe seguro — não cobramos durante os 7 dias)', 'Para cancelar: Definições → Gerir Subscrição → Cancel'],
        screenshot: 'A primeira cobrança só acontece no 8º dia. Cancela antes e não pagas nada. Plano Agency inclui workspaces por marca/cliente.',
      },
    ],
  },
  en: {
    badge: 'Official guide · 6 min read',
    heroTitle1: 'How to use',
    heroSub: 'From first login to your first cloned funnel in under 1 hour. Follow the 10 steps below in order — you\'ll see the platform\'s real power.',
    back: 'Back',
    visualTip: 'Visual tip',
    quickRefTitle: 'Quick Reference',
    quickRefSub: 'Shortcuts and tips to get the most out of it',
    urlsTitle: 'Accepted URLs',
    urls: ['✓ Sales pages (any platform)', '✓ Landing pages (Hotmart, Kiwify, Eduzz, etc)', '✓ Own websites (Wordpress, ClickFunnels)', '✗ Sites requiring login', '✗ Native apps (web only)'],
    freqTitle: 'Frequency',
    freq: ['• Instant analysis (~30s)', '• Monitoring: every 6h', '• CRM follow-up agenda: every morning', '• Weekly email summary (Monday)', '• Alerts: instant when something changes'],
    notifTitle: 'Notifications',
    notifs: ['📧 Welcome email (sign-up)', '📧 Payment confirmation', '📧 Daily CRM follow-up agenda (only when something is due)', '📧 Weekly summary (Monday)', '📧 Monitor change alerts'],
    settingsTitle: 'Settings',
    settingsItems: ['• Manage subscription (Stripe Portal)', '• Export all your data (GDPR)', '• Delete account permanently', '• Update payment method'],
    ctaTitle: 'Ready for your first funnel hack?',
    ctaSub: '7 days free. Unlimited analysis. Cancel anytime.',
    ctaBtn: 'Start free trial',
    footerHome: 'Home', footerPricing: 'Pricing', footerFaq: 'FAQ',
    steps: [
      {
        title: 'Create your account in 30 seconds',
        intro: 'It\'s free to start — 7-day trial on any paid plan. No card required upfront.',
        items: ['Click "Sign in" at the top and use your Google account', 'You\'re redirected to the Dashboard automatically', 'Your Free plan activates instantly (with 3 free monitors)'],
        screenshot: 'On first login you\'ll see a welcome wizard that takes you from analysis to your first copy generation in 4 steps.',
      },
      {
        title: 'Analyse your first competitor funnel',
        intro: 'Funnel Spy is the heart of the app. Paste any URL and the AI dissects it in seconds.',
        items: ['In the sidebar, click "Funnel Spy"', 'Paste the full sales page URL (e.g. https://example.com/offer)', 'Click "Analyse Funnel" — takes ~30 seconds', 'You\'ll see: extracted headlines, detected offers, funnel structure, quality score, estimated conversion and average ticket', 'At the end, click "Save to Library" to revisit later'],
        screenshot: 'The AI also detects order bumps, upsells, downsells and social proof elements automatically.',
      },
      {
        title: 'Score pages with Winner Score™ (and earn credits)',
        intro: 'Winner Score™ evaluates any page across 25+ indicators (headline, offer, proof, risk) and returns a 0-100 score with a fix plan.',
        items: ['In the menu, click "Winner Score" and paste the URL (yours or a competitor\'s)', 'You get the score, strengths, weaknesses and profit/scale potential', 'Enable "Publish anonymously to the Hall of Winners" — analyses scoring ≥ 75 join the public ranking (URL hidden)', '🏆 Bonus: each strong publication earns you +2 free credits (max 3/day)', 'Browse the public Hall of Winners to see winning funnel patterns by niche'],
        screenshot: 'Score YOUR page before spending on traffic — the fix plan tells you exactly what to improve.',
      },
      {
        title: 'Discover winning funnels in your niche (Discovery)',
        intro: 'Not sure which competitors to analyse? Discovery finds the funnels and ads scaling in your niche for you.',
        items: ['In the menu, click "Discovery" and type your niche (e.g. "weight loss", "side income")', 'The AI returns the relevant players with ready-made search queries', 'Each result has 4 direct buttons: FB Ads, Google, YT Ads and TikTok — see their active ads in 1 click', '"Manual shortcuts" open the Meta, Google/YouTube and TikTok ad libraries pre-filtered for your niche', 'Found an interesting funnel? Paste the URL into Funnel Spy and dissect it'],
        screenshot: 'Discovery + Funnel Spy together = an infinite pipeline of market-validated inspiration.',
      },
      {
        title: 'Generate your own persuasive copy',
        intro: 'The AI Composer creates headlines, VSLs, emails and bullets based on classic copywriting (Dan Kennedy, Russell Brunson, etc).',
        items: ['In the menu, click "AI Composer"', 'Choose the type: Headlines, VSL Script, Email Sequence, Persuasive Bullets', 'Describe the product/offer in 1-2 sentences', 'The AI generates 3-5 variations in ~15 seconds', 'Copy, edit or regenerate as many times as you like'],
        screenshot: 'For even better results, analyse a competitor funnel first and use the insights as context.',
      },
      {
        title: 'Monitor competitors 24/7 (automated)',
        intro: 'The system checks your competitors\' funnels every 6 hours and alerts you when they change anything.',
        items: ['In the menu, click "Monitoring"', 'Add a URL to monitor (the sales page URL)', 'Give it a name ("Competitor A — Sales Course") to identify it', 'The app captures periodic snapshots automatically', 'You get an email when there are changes (headlines, prices, structure)', 'Click a monitor to see a visual "diff"-style Timeline — you see exactly what changed'],
        screenshot: 'Free: 3 monitors · Pro: 25 monitors · Agency: unlimited. Everything checked every 6h.',
      },
      {
        title: 'Manage your leads in the CRM (with follow-ups and AI)',
        intro: 'A complete CRM with Kanban pipeline, deal value per contact and a follow-up agenda so you never lose a sale.',
        items: ['In the menu, click "CRM" and add contacts (or import a CSV from another tool)', 'Drag cards between stages: New → Contacted → Qualified → Proposal → Won', 'Set the value (€) and next-action date on each contact — the top shows total pipeline and weighted forecast', '"Overdue"/"Today" badges show who needs attention; use the follow-up filter', 'Inside each contact: activity timeline + "AI follow-up email" button generates a ready-to-send email', 'Every morning you get an email with your follow-up agenda for the day (only if something is due)'],
        screenshot: 'The AI score (✨ button on each card) rates the closing probability 0-100 with reasoning.',
      },
      {
        title: 'Build your inspiration library',
        intro: 'Every analysis can be saved. Build a private collection of the best funnels in your niche.',
        items: ['After any Funnel Spy analysis, click "Save to Library"', 'Access it via "Library" in the sidebar', 'Filter by niche, date or score', 'Revisit old analyses for inspiration on new launches'],
        screenshot: 'Your analyses are fully private. No other user sees what you\'re studying.',
      },
      {
        title: 'Credits, gifts and affiliates',
        intro: 'Don\'t want a subscription? Use credit packs. Want to earn with the app? Become an affiliate.',
        items: ['In "Credits": packs from €9 to €197 (1 credit = 1 premium generation, never expires)', '"As a gift" mode: buy a pack for someone else — they receive a code by email', 'Earn free credits: +2 for publishing analyses to the Hall of Winners, +10 for each Pro signup via Battle Card', 'In "Affiliates": get your unique link and earn recurring commissions for every subscriber you bring'],
        screenshot: 'The affiliate leaderboard shows the month\'s top promoters — with 1-click share buttons.',
      },
      {
        title: 'Upgrade when you\'re ready',
        intro: '7 days free on any paid plan. No long commitment, cancel with 1 click.',
        items: ['In the sidebar click "Pricing"', 'Compare Pro (€27/mo, 25 monitors) and Agency (€97/mo, unlimited + multi-brand)', 'Click the chosen plan\'s button to start the trial', 'Add a card (secure Stripe — we don\'t charge during the 7 days)', 'To cancel: Settings → Manage Subscription → Cancel'],
        screenshot: 'The first charge only happens on day 8. Cancel before and you pay nothing. The Agency plan includes per-brand/client workspaces.',
      },
    ],
  },
};

const StepCard = ({ n, icon: Icon, color, title, intro, items, screenshot, visualTip }) => (
  <div className="relative pl-12 md:pl-20 pb-12 border-l-2 border-white/10 last:border-l-transparent">
    <div
      className="absolute -left-[21px] md:-left-[25px] top-0 w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center font-[Outfit] font-bold text-sm bg-[#0A0A0A] border-2"
      style={{ borderColor: color, color }}
    >
      {n.toString().padStart(2, '0')}
    </div>
    <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-6 md:p-8">
      <div className="flex items-center gap-3 mb-4">
        <div
          className="w-11 h-11 rounded-xl flex items-center justify-center"
          style={{ backgroundColor: `${color}22` }}
        >
          <Icon size={22} weight="bold" style={{ color }} />
        </div>
        <h3 className="text-xl md:text-2xl font-[Outfit] font-bold">{title}</h3>
      </div>
      <p className="text-white/70 leading-relaxed mb-4">{intro}</p>
      <ul className="space-y-2.5 mb-4">
        {items.map((it, idx) => (
          <li key={idx} className="flex items-start gap-2.5 text-sm text-white/80 leading-relaxed">
            <Check size={16} weight="bold" className="text-[#10B981] flex-shrink-0 mt-1" />
            <span>{it}</span>
          </li>
        ))}
      </ul>
      {screenshot && (
        <div className="mt-5 p-4 bg-black/40 border border-dashed border-white/15 rounded-xl">
          <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-2">{visualTip}</div>
          <div className="text-sm text-white/70 italic">{screenshot}</div>
        </div>
      )}
    </div>
  </div>
);

const RefCard = ({ icon: Icon, color, title, items }) => (
  <div className="p-6 bg-white/[0.02] border border-white/10 rounded-2xl">
    <div className="flex items-center gap-2 mb-3">
      <Icon size={18} weight="bold" style={{ color }} />
      <h3 className="font-[Outfit] font-bold">{title}</h3>
    </div>
    <ul className="text-sm text-white/70 space-y-1.5">
      {items.map((it, i) => <li key={i}>{it}</li>)}
    </ul>
  </div>
);

const Guia = () => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const c = CONTENT[i18n.language?.startsWith('en') ? 'en' : 'pt'];

  useEffect(() => {
    document.title = i18n.language?.startsWith('en') ? 'Guide · FunnelHack AI' : 'Guia · FunnelHack AI';
  }, [i18n.language]);

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="guia-page">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 bg-black/60 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="guide-home-link">
            <div className="w-10 h-10 bg-gradient-to-br from-[#2563EB] to-[#3B82F6] rounded-lg flex items-center justify-center">
              <ChartLine size={24} weight="bold" className="text-white" />
            </div>
            <span className="text-xl font-[Outfit] font-bold tracking-tight">FunnelHack AI</span>
          </Link>
          <button
            onClick={() => navigate(-1)}
            data-testid="guide-back-btn"
            className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-sm transition flex items-center gap-1.5"
          >
            <CaretLeft size={16} weight="bold" />
            {c.back}
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-36 pb-16 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full mb-6">
            <Lightning size={14} weight="fill" className="text-[#3B82F6]" />
            <span className="text-xs text-white/80 uppercase tracking-[0.25em]">{c.badge}</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-[Outfit] font-bold tracking-tight mb-5">
            {c.heroTitle1}<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#2563EB] to-[#3B82F6]">FunnelHack AI</span>
          </h1>
          <p className="text-white/60 text-base md:text-lg leading-relaxed">
            {c.heroSub}
          </p>
        </div>
      </section>

      {/* Steps */}
      <section className="px-6 pb-20">
        <div className="max-w-3xl mx-auto">
          {c.steps.map((s, i) => (
            <StepCard key={i} n={i + 1} {...STEP_META[i]} {...s} visualTip={c.visualTip} />
          ))}
        </div>
      </section>

      {/* Quick reference */}
      <section className="px-6 pb-24">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-[Outfit] font-bold tracking-tight mb-3">
              {c.quickRefTitle}
            </h2>
            <p className="text-white/60">{c.quickRefSub}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <RefCard icon={Cursor} color="#3B82F6" title={c.urlsTitle} items={c.urls} />
            <RefCard icon={Clock} color="#F59E0B" title={c.freqTitle} items={c.freq} />
            <RefCard icon={Bell} color="#10B981" title={c.notifTitle} items={c.notifs} />
            <RefCard icon={Gear} color="#A855F7" title={c.settingsTitle} items={c.settingsItems} />
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="px-6 pb-24">
        <div className="max-w-3xl mx-auto p-10 bg-gradient-to-br from-[#2563EB]/15 to-transparent border border-[#2563EB]/30 rounded-3xl text-center">
          <Target size={40} weight="bold" className="text-[#3B82F6] mx-auto mb-4" />
          <h2 className="text-3xl md:text-4xl font-[Outfit] font-bold tracking-tight mb-3">
            {c.ctaTitle}
          </h2>
          <p className="text-white/60 mb-8">
            {c.ctaSub}
          </p>
          <button
            onClick={handleLogin}
            data-testid="guide-cta-btn"
            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-[#2563EB] to-[#3B82F6] rounded-xl font-semibold transition hover:opacity-90 shadow-2xl shadow-blue-500/30"
          >
            <Rocket size={20} weight="fill" />
            {c.ctaBtn}
            <ArrowRight size={18} weight="bold" />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-6 py-8 border-t border-white/10 text-sm text-white/40">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div>© {new Date().getFullYear()} FunnelHack AI</div>
          <div className="flex items-center gap-6">
            <Link to="/" className="hover:text-white transition">{c.footerHome}</Link>
            <a href="/#pricing" className="hover:text-white transition">{c.footerPricing}</a>
            <a href="/#faq" className="hover:text-white transition">{c.footerFaq}</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Guia;
